/**
 * Helper pur ( zéro dépendance Vue / i18n / DOM ) résolvant la clé i18n
 * du tooltip d'aide à afficher à côté du label d'un filtre dans le bloc
 * {@code FiltersDataCollapse} .
 *
 * <p><b>Objectif</b> : centraliser la connaissance "quel type de filtre
 * mérite quel libellé d'aide" pour que tous les blocs de filtres
 * ( référence autogénérée , FILTER_LIST , FILTER_TEXT , range numérique ,
 * range date ) consomment un unique composant {@code FilterHelpTooltip}
 * sans dupliquer la logique d'aiguillage côté template .
 *
 * <p><b>Propriétés SOLID</b> :
 * <ul>
 *   <li><i>SRP</i> : ne fait QUE le mapping ( type checker / tags ) -&gt; cle i18n ;
 *       aucun rendu , aucun side-effect ;</li>
 *   <li><i>OCP</i> : ajouter un nouveau type de filtre ( ex BooleanChecker )
 *       = ajouter une branche dans {@link resolveFilterHelpKind} + une clé
 *       dans {@link FILTER_HELP_I18N_KEYS} + i18n FR/EN . Pas de modification
 *       des consumers existants ;</li>
 *   <li><i>DRY</i> : la table {@link FILTER_HELP_I18N_KEYS} est la seule
 *       source de vérité pour le mapping kind -&gt; clé ;</li>
 *   <li><i>Testable</i> : aucun import Vue , fonction pure 100 % vérifiable
 *       en isolation .</li>
 * </ul>
 */

/**
 * Catégorie métier de filtre dont dépend le libellé d'aide à afficher .
 * {@code null} = pas d'aide spécifique pour cette colonne ( ex colonne
 * non-filtrable , type checker non géré ) .
 */
export type FilterHelpKind = "reference" | "list" | "text" | "numeric" | "date" | null;

/**
 * Vue minimale d'une colonne suffisant à dériver son type de filtre .
 * Le champ {@code checker.type} est l'identifiant Java pénétré dans la
 * config YAML ( {@code ReferenceChecker} , {@code FloatChecker} , ... ) .
 */
export interface FilterableColumnLike {
  checker?: { type?: string } | null;
}

/**
 * Indicateurs des tags OA déclaratifs ( {@code __FILTER_LIST__} /
 * {@code __FILTER_TEXT__} ) appliqués à la colonne . Quand un tag est
 * actif , il PRÉVAUT sur le type natif du checker ( pattern existant
 * dans {@code FiltersDataCollapse} ) .
 */
export interface FilterTagFlags {
  hasList?: boolean;
  hasText?: boolean;
}

/** Types de checker considérés comme "range numérique" . Étendre ici pour OCP . */
const NUMERIC_CHECKER_TYPES: ReadonlySet<string> = new Set([
  "FloatChecker",
  "IntegerChecker",
]);

/** Types de checker considérés comme "range date" . Étendre ici pour OCP . */
const DATE_CHECKER_TYPES: ReadonlySet<string> = new Set([
  "DateChecker",
]);

/**
 * Table unique des clés i18n par catégorie de filtre . Source de vérité
 * pour les consumers Vue ( composant {@code FilterHelpTooltip} ) , évite
 * la dispersion des littéraux dans les templates .
 */
export const FILTER_HELP_I18N_KEYS: Readonly<Record<Exclude<FilterHelpKind, null>, string>> = Object.freeze({
  reference: "filter.reference-tooltip-help",
  list: "filter.list-tooltip-help",
  text: "filter.text-tooltip-help",
  numeric: "filter.numeric-tooltip-help",
  date: "filter.date-tooltip-help",
});

/**
 * Détermine la catégorie de tooltip d'aide pour une colonne filtrable .
 *
 * <p><b>Priorité</b> ( aligné sur le comportement historique de
 * {@code FiltersDataCollapse} ) :
 * <ol>
 *   <li>tag {@code __FILTER_LIST__} -&gt; {@code list} ;</li>
 *   <li>tag {@code __FILTER_TEXT__} -&gt; {@code text} ;</li>
 *   <li>type checker {@code ReferenceChecker} -&gt; {@code reference} ;</li>
 *   <li>type checker {@code FloatChecker} / {@code IntegerChecker}
 *       -&gt; {@code numeric} ;</li>
 *   <li>type checker {@code DateChecker} -&gt; {@code date} ;</li>
 *   <li>sinon -&gt; {@code null} ( pas d'aide ) .</li>
 * </ol>
 */
export function resolveFilterHelpKind(
  column: FilterableColumnLike | null | undefined,
  tags?: FilterTagFlags | null,
): FilterHelpKind {
  if (!column) return null;
  if (tags?.hasList) return "list";
  if (tags?.hasText) return "text";
  const type = column.checker?.type;
  if (!type) return null;
  if (type === "ReferenceChecker") return "reference";
  if (NUMERIC_CHECKER_TYPES.has(type)) return "numeric";
  if (DATE_CHECKER_TYPES.has(type)) return "date";
  return null;
}

/**
 * Cle i18n associée à un {@link FilterHelpKind} ; {@code null} si le
 * kind est {@code null} ( pas de tooltip à rendre ) .
 */
export function resolveFilterHelpI18nKey(kind: FilterHelpKind): string | null {
  return kind ? FILTER_HELP_I18N_KEYS[kind] : null;
}
